import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { LuArrowRight, LuX } from 'react-icons/lu';
import useEntitlement from '../../hooks/useEntitlement';
import useStartTrial from '../../hooks/useStartTrial';
import { COPY } from '../../contracts/copyMap';
import { ROUTES } from '../../contracts/routes';

// ProBanner — the persistent membership offer at the top of the app shell.
//
// ── THIS IS A SANCTIONED EXCEPTION TO DESIGN.md §2.2 ────────────────────────
//
// §2.2 says "Aurum as a flood fill on backgrounds, buttons, or large surfaces"
// is forbidden — Aurum is type, stroke, or hairline only. This bar is a full
// Aurum surface, which is exactly that pattern.
//
// It ships as a NAMED exception in §2.2 rather than as a quiet violation. The
// distinction matters: a written rule that the codebase silently breaks is
// worse than no rule, because the next person cannot tell which parts of the
// document are real. The rule was changed, on purpose, and says so.
//
// The exception is deliberately narrow — ONE bar, ONE per screen, dismissible.
// It is the only Aurum flood fill in the product, and the Censor Bar and brand
// mark stay untouched by it.
//
// ── WHERE IT DOES NOT APPEAR ────────────────────────────────────────────────
//
//   member / trialing   there is nothing to sell them
//   entitlement loading suppressed rather than flashed — a bar that appears
//                       and vanishes on every navigation reads as a bug
//   entitlement ERROR   HIDDEN, inverting the usual fail-closed rule. The
//                       failure mode here is showing a sales pitch to someone
//                       who is already paying, which is worse than a missed
//                       impression. requireEntitlement fails closed because a
//                       paid FEATURE is at stake; nothing is at stake here.
//   /membership         they are already reading the offer
//   fitViewport pages   Create pins itself to 100dvh with overflow hidden, so
//                       there is no spare vertical room by construction and a
//                       bar would squeeze the capture viewport on small
//                       phones. Create carries UnlockGateModal as its upsell.
//   dismissed           remembered, see below
//
// ── DISMISSAL ───────────────────────────────────────────────────────────────
//
// Persisted with a timestamp rather than a boolean, so the offer returns after
// a cooling-off window instead of being gone forever after one stray tap.
// Same shape MigrationBanner used, which this replaces as the app's banner
// pattern — that component was dead code and is deleted alongside this.

const DISMISSED_KEY = 'mogged_pro_banner_dismissed_at';
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

export function isProBannerDismissed(now = Date.now()) {
  if (typeof window === 'undefined') return true;
  try {
    const at = window.localStorage.getItem(DISMISSED_KEY);
    if (!at) return false;
    const parsed = parseInt(at, 10);
    if (Number.isNaN(parsed)) return false;
    return now - parsed < COOLDOWN_MS;
  } catch (_e) {
    // Private-mode Safari throws on localStorage. Showing the banner is the
    // right failure: it is recoverable by dismissing again, whereas hiding it
    // forever on a storage error silently deletes the surface.
    return false;
  }
}

export default function ProBanner({ suppressed = false }) {
  const { isPro, isLoading, error } = useEntitlement();
  const { pathname } = useLocation();
  const { startTrial, submitting } = useStartTrial('ProBanner');
  const [dismissed, setDismissed] = useState(() => isProBannerDismissed());

  if (suppressed || dismissed || isPro || isLoading || error) return null;
  if (pathname === ROUTES.MEMBERSHIP) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch (_e) {
      // Dismissal still applies for this session; it just will not persist.
    }
  };

  return (
    <div
      data-testid="pro-banner"
      // `relative` + `overflow-hidden` contain the sweep; the bar itself is the
      // Aurum plate. Dark type on gold, never Aurum-on-Aurum (§2.2).
      className="relative overflow-hidden bg-aurum-plate"
    >
      {/* The shimmer. A single translucent bone band travelling across the
          plate — NOT a second hue, and not a colour gradient. motion-safe so it
          disappears entirely under prefers-reduced-motion. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 motion-safe:animate-[foil-sweep_4.5s_ease-in-out_infinite]"
        style={{
          background:
            'linear-gradient(100deg, transparent 0%, rgba(255,255,255,0.45) 50%, transparent 100%)',
          width: '40%',
        }}
      />

      <div className="relative flex items-center gap-3 px-5 lg:px-12 py-2.5">
        <button
          type="button"
          onClick={startTrial}
          disabled={submitting}
          data-testid="pro-banner-cta"
          className="flex min-w-0 flex-1 items-center gap-2 bg-transparent border-0 p-0 text-left cursor-pointer disabled:cursor-wait"
        >
          {/* font-display, NOT font-censor. The Censor Bar face is reserved for
              Censor Bar contents (DESIGN.md §2) and this gold plate is not one —
              borrowing the face made the bar read as a piece of brand furniture
              rather than something you press. The serif plus a bold weight and
              an underline reads as a control.

              font-bold is real here, not synthesized: Cormorant Garamond 600/700
              are loaded in public/index.html. Faux bold on a serif at 13px is a
              smear. */}
          <span className="font-display font-bold underline underline-offset-[3px] decoration-obsidian/45 text-obsidian text-[14px] lg:text-[15px] shrink-0">
            {COPY.PRO_BANNER_HEADLINE}
          </span>
          <span className="hidden sm:inline font-display text-obsidian/70 text-[12.5px] truncate">
            {COPY.PRO_BANNER_SUBTITLE}
          </span>
          <LuArrowRight size={15} className="shrink-0 text-obsidian" aria-hidden="true" />
        </button>

        <button
          type="button"
          onClick={dismiss}
          aria-label={COPY.PRO_BANNER_DISMISS_LABEL}
          data-testid="pro-banner-dismiss"
          className="shrink-0 bg-transparent border-0 p-1 cursor-pointer text-obsidian/55 hover:text-obsidian transition-colors duration-150"
        >
          <LuX size={15} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
